import React, { useEffect, useState } from 'react';
import { TimePicker } from 'antd';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import ImageUploader from '../../../components/ImageUploader';
import './OnboardingSetup.css';
import { Link } from 'react-router-dom';
import Logo from '../../../components/Logomenu';
import classNames from 'classnames';
import useDarkMode from '../../../hooks/useDarkMode';
import Button from '../../../components/bootstrap/Button';
import Popovers from '../../../components/bootstrap/Popovers';
import LANG, { getLangWithKey } from '../../../lang';
import { useTranslation } from 'react-i18next';
import Dropdown, {
	DropdownItem,
	DropdownMenu,
	DropdownToggle,
} from '../../../components/bootstrap/Dropdown';
import Page from '../../../layout/Page/Page';
import PageWrapper from '../../../layout/PageWrapper/PageWrapper';
import Card, { CardBody } from '../../../components/bootstrap/Card';
import { ClockCircleOutlined } from '@ant-design/icons';
import '../demo-pages/time-picker-override.css';
import Select from '../../../components/bootstrap/forms/Select';
import Paypal from '../../../assets/img/paypal.svg';
import showNotification from '../../../components/extras/showNotification';
import Icon from '../../../components/icon/Icon';

const steps = ['Welcome', 'Business Info', 'Choose Plan', 'Domain Setup', 'Review', 'Payment'];

const plans = [
	{
		name: 'Basic',
		price: 4900,
		currency: 'Rs.',
		period: 'MONTHLY',
		users: '30+',
		highlight: false,
		features: [
			'Team Members – 3',
			'Administrators – 2',
			'Customers – Unlimited',
			'Services – 5',
			'Products – 50',
		],
		button: 'Get this Plan',
		note: 'Free Basic Website – Yes (6 Months subscription required)',
	},
	{
		name: 'Popular',
		price: 7900,
		currency: 'Rs.',
		period: 'MONTHLY',
		users: '60+',
		highlight: true,
		features: [
			'Team Members – 5',
			'Administrators – 2',
			'Customers – Unlimited',
			'Services – 10',
			'Products – 300',
		],
		button: 'Get this Plan',
		note: 'Free Basic Website – Yes (3 Months subscription required)',
	},
	{
		name: 'Premium',
		price: 11900,
		currency: 'Rs.',
		period: 'MONTHLY',
		users: '10+',
		highlight: false,
		features: [
			'Team Members – 20',
			'Administrators – 4',
			'Customers – Unlimited',
			'Services – 30',
			'Products – Unlimited',
		],
		button: 'Get this Plan',
		note: 'Free Basic Website – Yes',
	},
	{
		name: 'Exclusive',
		price: 14900,
		currency: 'Rs.',
		period: 'MONTHLY',
		users: '9+',
		highlight: false,
		features: [
			'Team Members – Unlimited',
			'Administrators – 5',
			'Customers – Unlimited',
			'Services – Unlimited',
			'Products – Unlimited',
		],
		button: 'Get this Plan',
		note: 'Free Basic Website – Yes',
	},
];

const defaultBusinessInfo = {
	salonName: '',
	ownerName: '',
	address: '',
	country: '',
	contactNumber: '',
	whatsappNumber: '',
	email: '',
	businessType: '',
	businessTypeOther: '',
	taxId: '',
	currency: 'USD',
	language: 'English',
	hours: [{ day: 'Monday', open: '', close: '' }],
	logoWhite: [],
	logoDark: [],
	logoMobile: [],
};

const OnboardingSetup = () => {
	const [step, setStep] = useState(0);
	const [businessInfo, setBusinessInfo] = useState(defaultBusinessInfo);
	const [selectedPlan, setSelectedPlan] = useState(null);
	const [domainOption, setDomainOption] = useState('temporary');
	const [customDomains, setCustomDomains] = useState({
		customer: '',
		employee: '',
		business: '',
		webapi: '',
		cdn: '',
	});
	const [tempDomains, setTempDomains] = useState({
		customer: '',
		employee: '',
		business: '',
		webapi: '',
		cdn: '',
	});
	const [reviewAccepted, setReviewAccepted] = useState(false);
	const [termsAccepted, setTermsAccepted] = useState(false);
	const [paymentOption, setPaymentOption] = useState('trial');
	const [bankReceipt, setBankReceipt] = useState([]);
	const [errors, setErrors] = useState({});
	const [billingPeriod, setBillingPeriod] = useState('monthly');
	const [bankInputKey, setBankInputKey] = useState(Date.now());
	const [paymentMethod, setPaymentMethod] = useState('trial');
	const [localBankReceipt, setLocalBankReceipt] = useState([]);
	const [bankReceiptError, setBankReceiptError] = useState('');
	const [previewImg, setPreviewImg] = useState(null);
	const navigate = useNavigate && useNavigate();
	const { darkModeStatus, setDarkModeStatus, themeStatus } = useDarkMode();
	const { t } = useTranslation();
	const { i18n } = useTranslation();

	useEffect(() => {
		const storedTheme = localStorage.getItem('theme');
		if (storedTheme !== null) {
			setDarkModeStatus(storedTheme === 'dark');
		}
	}, [setDarkModeStatus]);

	useEffect(() => {
		const currentLang = getLangWithKey(i18n.language);

		if (!currentLang) {
			i18n.changeLanguage(LANG.EN.lng);
		}
	}, []);

	const changeLanguage = (lng) => {
		i18n.changeLanguage(lng);
		showNotification(
			<span className='d-flex align-items-center'>
				<Icon icon={getLangWithKey(lng)?.icon} size='lg' className='me-1' />
				<span>{`Language changed to ${getLangWithKey(lng)?.text}`}</span>
			</span>,
		);
	};

	const styledBtn = {
		color: darkModeStatus ? 'dark' : 'light',
		hoverShadow: 'default',
		isLight: !darkModeStatus,
		size: 'lg',
	};

	const renderWelcome = () => (
		<div
			style={{
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				justifyContent: 'center',
				height: 'calc(80vh - 80px)',
				minHeight: 400,
				textAlign: 'center',
			}}>
			<Logo width={200} />
			<h1 style={{ fontWeight: 700, marginBottom: 8, marginTop: 16 }}>
				Welcome to Business Setup
			</h1>
			<div style={{ color: '#666', fontSize: '1.5rem', marginBottom: 32 }}>
				Let’s get your business up and running in just a few easy steps.
				<br />
				Please follow the guided setup below.
			</div>
			<button className='onboarding-btn' onClick={() => setStep(1)}>
				Start Setup
			</button>
		</div>
	);

	const businessTypes = ['Salon', 'Spa', 'Nail Studio', 'Unisex', 'Other'];
	const currencies = ['USD', 'LKR', 'AED', 'EUR', 'INR', 'GBP'];
	const languages = ['English', 'Sinhala', 'Tamil', 'French', 'German'];
	const daysOfWeek = [
		'Monday',
		'Tuesday',
		'Wednesday',
		'Thursday',
		'Friday',
		'Saturday',
		'Sunday',
	];

	const handleBusinessInfoChange = (e) => {
		const { name, value } = e.target;
		setBusinessInfo((prev) => {
			if (name === 'businessType') {
				return {
					...prev,
					businessType: value,
					businessTypeOther: value === 'Other' ? prev.businessTypeOther : '',
				};
			}
			return { ...prev, [name]: value };
		});
		setErrors((prev) => ({ ...prev, [name]: '' }));
	};

	const handleHoursChange = (idx, field, value) => {
		setBusinessInfo((prev) => ({
			...prev,
			hours: prev.hours.map((h, i) => (i === idx ? { ...h, [field]: value } : h)),
		}));
	};

	const handleAddDay = () => {
		if (businessInfo.hours.length < 7) {
			const nextDay = daysOfWeek[businessInfo.hours.length];
			setBusinessInfo((prev) => ({
				...prev,
				hours: [...prev.hours, { day: nextDay, open: '', close: '' }],
			}));
		}
	};

	const handleLogoChange = (type) => (e) => {
		const files = Array.from(e.target.files || []);
		if (!files.length) return;
		const fileReaders = files.map((file) => {
			return new Promise((resolve) => {
				const reader = new FileReader();
				reader.onload = () =>
					resolve({
						id: Date.now() + Math.random(),
						file: reader.result,
						name: file.name,
					});
				reader.readAsDataURL(file);
			});
		});
		Promise.all(fileReaders).then((images) => {
			setBusinessInfo((prev) => ({
				...prev,
				[type]: images,
			}));
		});
	};

	const handleDeleteDay = (idx) => {
		setBusinessInfo((prev) => ({
			...prev,
			hours: prev.hours.filter((_, i) => i !== idx),
		}));
	};

	const TEMP_DOMAIN_SUFFIX = 'schedulerdesk.com';
	const [domainStep, setDomainStep] = useState('intro');
	const [tempGenLoading, setTempGenLoading] = useState(false);
	const [tempGenDomains, setTempGenDomains] = useState(null);
	const [tempGenStatus, setTempGenStatus] = useState(null);
	const [tempGenError, setTempGenError] = useState(null);
	const [customStatus, setCustomStatus] = useState({});
	const [showDnsHelp, setShowDnsHelp] = useState(false);

	const generateTempDomains = () => {
		setTempGenLoading(true);
		setTempGenError(null);
		const brand = 'tempbrand' + Math.floor(Math.random() * 1000);
		const domains = {};
		Object.keys(tempDomains).forEach((key) => {
			domains[key] = `${key}.${brand}.${TEMP_DOMAIN_SUFFIX}`;
		});
		setTimeout(() => {
			setTempGenDomains(domains);
			setTempGenStatus(Object.fromEntries(Object.keys(domains).map((k) => [k, 'loading'])));
			setTimeout(() => {
				const status = {};
				Object.keys(domains).forEach((k) => {
					// status[k] = Math.random() > 0.1 ? 'success' : 'fail';
					status[k] = 'success';
				});
				setTempGenStatus(status);
				setTempGenLoading(false);
				if (Object.values(status).includes('fail')) {
					setTempGenError('Some domains failed validation. Please re-generate.');
				}
			}, 1200);
		}, 1000);
	};

	const validateCustomDomain = (key) => {
		setCustomStatus((prev) => ({ ...prev, [key]: 'loading' }));
		setTimeout(() => {
			setCustomStatus((prev) => ({
				...prev,
				[key]: Math.random() > 0.2 ? 'success' : 'fail',
			}));
		}, 1200);
	};

	const validateAllCustomDomains = () => {
		Object.keys(customDomains).forEach((key) => {
			if (customDomains[key]) validateCustomDomain(key);
		});
	};

	const allTempValid =
		tempGenStatus && Object.values(tempGenStatus).every((s) => s === 'success');
	const allCustomValid =
		Object.keys(customDomains).length > 0 &&
		Object.keys(customDomains).every((key) => customStatus[key] === 'success');

	const renderDomainIntro = () => {
		const getCardStyle = (selected, type) => {
			if (themeStatus === 'dark') {
				return {
					minWidth: 220,
					maxWidth: 260,
					background: selected ? '#23263a' : '#181a25',
					border: selected ? '2px solid #4d69fa' : '2px solid #23263a',
					cursor: 'pointer',
					boxShadow: selected ? '0 4px 24px #4d69fa22' : undefined,
					transition: 'box-shadow 0.2s, border 0.2s',
				};
			} else {
				return {
					minWidth: 220,
					maxWidth: 260,
					background: selected ? '#eaf0ff' : '#f8f9fa',
					border: selected ? '2px solid #4d69fa' : '2px solid #eee',
					cursor: 'pointer',
					boxShadow: selected ? '0 4px 24px #4d69fa22' : undefined,
					transition: 'box-shadow 0.2s, border 0.2s',
				};
			}
		};
		const getTitleColor = () => (themeStatus === 'dark' ? '#fff' : '#23263a');
		return (
			<div
				style={{
					maxWidth: 600,
					margin: '0 auto',
					textAlign: 'center',
					minHeight: '65vh',
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'center',
					alignItems: 'center',
					height: '100%',
				}}>
				<h2 style={{ marginBottom: 8, fontWeight: 800, letterSpacing: 0.5 }}>
					Point Your Domain
				</h2>
				<div style={{ fontSize: 18, color: '#4d69fa', marginBottom: 8, fontWeight: 600 }}>
					Choose how you want to set up your domain.
				</div>
				<div style={{ color: '#888', marginBottom: 32, fontSize: 16 }}>
					You can use your own domain or proceed with a temporary one now and connect your
					domain later.
				</div>
				<div
					style={{
						display: 'flex',
						justifyContent: 'center',
						gap: 32,
						marginBottom: 32,
					}}>
					<div
						className={`onboarding-card${
							domainOption === 'temporary' ? ' selected' : ''
						}`}
						style={getCardStyle(domainOption === 'temporary', 'temporary')}
						onClick={() => setDomainOption('temporary')}>
						<div
							style={{
								fontWeight: 700,
								fontSize: 20,
								marginBottom: 8,
								color: getTitleColor(),
							}}>
							Use Temporary Domain
						</div>
						<div style={{ color: '#888', fontSize: 15 }}>
							Get started instantly with a free temporary domain. You can connect your
							own later.
						</div>
					</div>
					<div
						className={`onboarding-card${domainOption === 'custom' ? ' selected' : ''}`}
						style={getCardStyle(domainOption === 'custom', 'custom')}
						onClick={() => setDomainOption('custom')}>
						<div
							style={{
								fontWeight: 700,
								fontSize: 20,
								marginBottom: 8,
								color: getTitleColor(),
							}}>
							Use Custom Domain
						</div>
						<div style={{ color: '#888', fontSize: 15 }}>
							Already have a domain? Connect it now for a branded experience.
						</div>
					</div>
				</div>
				<div style={{ display: 'flex', justifyContent: 'center', width: '100%', gap: 16 }}>
					<button
						type='button'
						className='onboarding-btn'
						style={{ background: '#aaa', minWidth: 120 }}
						onClick={() => setStep(2)}>
						Back
					</button>
					<button
						type='button'
						className='onboarding-btn'
						style={{ minWidth: 120 }}
						disabled={!domainOption}
						onClick={() => {
							if (domainOption === 'temporary') setDomainStep('temp');
							else if (domainOption === 'custom') setDomainStep('custom');
						}}>
						Continue
					</button>
				</div>
			</div>
		);
	};

	const renderTempDomainFlow = () => (
		<div
			style={{
				maxWidth: 600,
				margin: '0 auto',
				minHeight: '65vh',
				display: 'flex',
				flexDirection: 'column',
				justifyContent: 'center',
				alignItems: 'center',
				height: '100%',
			}}>
			<h2
				style={{
					textAlign: 'center',
					marginBottom: 8,
					fontWeight: 800,
					letterSpacing: 0.5,
				}}>
				Use Temporary Domain
			</h2>
			<div
				style={{
					color: '#4d69fa',
					textAlign: 'center',
					marginBottom: 24,
					fontWeight: 600,
					fontSize: 16,
				}}>
				We’ll generate temporary sub-domains for you to get started immediately.
				<br />
				<span style={{ color: '#888', fontWeight: 400 }}>
					You can connect your own domain anytime later.
				</span>
			</div>
			{!tempGenDomains ? (
				<>
					<div style={{ textAlign: 'center', marginBottom: 32 }}>
						<button
							type='button'
							className='onboarding-btn'
							style={{ minWidth: 220, fontWeight: 600 }}
							onClick={generateTempDomains}
							disabled={tempGenLoading}>
							{tempGenLoading ? 'Generating...' : 'Generate Temporary Domains'}
						</button>
					</div>
					<div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
						<button
							type='button'
							className='onboarding-btn'
							style={{ background: '#aaa', minWidth: 120 }}
							onClick={() => setDomainStep('intro')}>
							Back
						</button>
					</div>
				</>
			) : (
				<>
					<div style={{ marginBottom: 16, fontWeight: 600 }}>Generated Domains:</div>
					<div style={{ marginBottom: 16 }}>
						{Object.keys(tempGenDomains).map((key) => (
							<div
								key={key}
								style={{
									display: 'flex',
									alignItems: 'center',
									gap: 12,
									marginBottom: 8,
								}}>
								<span
									style={{
										width: 120,
										fontWeight: 500,
										textTransform: 'capitalize',
										color: '#4d69fa',
									}}>
									{key}:
								</span>
								<span style={{ fontFamily: 'monospace', fontSize: 15 }}>
									{tempGenDomains[key]}
								</span>
								{tempGenStatus && tempGenStatus[key] === 'loading' && (
									<span style={{ color: '#888' }}>
										<span className='spinner-border spinner-border-sm' />{' '}
										Validating...
									</span>
								)}
								{tempGenStatus && tempGenStatus[key] === 'success' && (
									<span style={{ color: 'green', fontWeight: 600 }}>
										✅ Validated
									</span>
								)}
								{tempGenStatus && tempGenStatus[key] === 'fail' && (
									<span style={{ color: 'red', fontWeight: 600 }}>❌ Failed</span>
								)}
							</div>
						))}
					</div>
					{tempGenLoading && (
						<div style={{ color: '#888', marginBottom: 12 }}>Validating domains...</div>
					)}
					{allTempValid && (
						<div style={{ color: 'green', fontWeight: 600, marginBottom: 16 }}>
							<span style={{ fontSize: 18 }}>✅ Validation successful!</span>
						</div>
					)}
					{tempGenError && (
						<div style={{ color: 'red', marginBottom: 16 }}>{tempGenError}</div>
					)}
					<div
						style={{
							display: 'flex',
							justifyContent: 'center',
							width: '100%',
							marginTop: 24,
						}}>
						<button
							type='button'
							className='onboarding-btn'
							style={{ background: '#aaa', minWidth: 120, marginRight: 12 }}
							onClick={() => {
								setTempGenDomains(null);
								setTempGenStatus(null);
								setDomainStep('intro');
							}}>
							Back
						</button>
						{!allTempValid && (
							<button
								type='button'
								className='onboarding-btn'
								onClick={generateTempDomains}
								style={{ marginRight: 12 }}>
								Re-generate Domains
							</button>
						)}
						<button
							type='button'
							className='onboarding-btn'
							disabled={!allTempValid}
							onClick={() => setStep(4)}>
							Proceed to Next Step
						</button>
					</div>
				</>
			)}
		</div>
	);

	const renderCustomDomainFlow = () => (
		<div
			style={{
				maxWidth: 700,
				margin: '0 auto',
				minHeight: '60vh',
				display: 'flex',
				flexDirection: 'column',
				justifyContent: 'center',
				alignItems: 'center',
				height: '100%',
			}}>
			<h2 style={{ textAlign: 'center', marginBottom: 8 }}>Use Your Own Domain</h2>
			<div style={{ color: '#888', textAlign: 'center', marginBottom: 18 }}>
				Enter your domain below. We’ll help you point the required sub-domains.
				<br />
				Make sure you’ve added the DNS records before validation.
			</div>
			<div
				style={{
					display: 'grid',
					gridTemplateColumns: '1fr 2fr 120px 120px',
					gap: 12,
					maxWidth: 650,
					margin: '0 auto',
					alignItems: 'center',
					marginBottom: 12,
				}}>
				<div style={{ fontWeight: 600 }}>Sub-Domain</div>
				<div style={{ fontWeight: 600 }}>Input</div>
				<div></div>
				<div></div>
				{Object.keys(customDomains).map((key) => (
					<React.Fragment key={key}>
						<div style={{ textTransform: 'capitalize', fontWeight: 500 }}>{key}</div>
						<input
							className='form-control mb-2 mt-2'
							name={key}
							value={customDomains[key]}
							onChange={(e) =>
								setCustomDomains((prev) => ({ ...prev, [key]: e.target.value }))
							}
							placeholder={`${key}.YOURDOMAIN.com`}
							style={{
								background: themeStatus === 'dark' ? '#23263a' : '#f8f9fa',
								color: themeStatus === 'dark' ? '#fff' : '#23263a',
							}}
						/>
						<button
							type='button'
							className='onboarding-btn'
							style={{ fontSize: 14, padding: '4px 10px', margin: 0 }}
							onClick={() => validateCustomDomain(key)}
							disabled={!customDomains[key] || customStatus[key] === 'loading'}>
							{customStatus[key] === 'loading' ? 'Validating...' : 'Validate'}
						</button>
						<div>
							{customStatus[key] === 'success' && (
								<span style={{ color: 'green', fontWeight: 600 }}>✅ Valid</span>
							)}
							{customStatus[key] === 'fail' && (
								<span style={{ color: 'red', fontWeight: 600 }}>❌ Not found</span>
							)}
						</div>
					</React.Fragment>
				))}
			</div>
			<div style={{ textAlign: 'right', marginBottom: 12, width: '100%' }}>
				<button
					type='button'
					className='onboarding-btn'
					style={{ fontSize: 14, marginRight: 8 }}
					onClick={validateAllCustomDomains}>
					Check all at once
				</button>
			</div>
			<div style={{ textAlign: 'right', marginBottom: 12, width: '100%' }}>
				<button
					type='button'
					className='onboarding-btn'
					style={{ fontSize: 14 }}
					onClick={() => setShowDnsHelp((v) => !v)}>
					{showDnsHelp ? 'Hide DNS Setup Help' : 'Show DNS Setup Help'}
				</button>
			</div>
			{showDnsHelp && (
				<div
					style={{
						background: themeStatus === 'dark' ? '#23263a' : '#f8f9fa',
						border: themeStatus === 'dark' ? '1px solid #35395a' : '1px solid #eee',
						borderRadius: 8,
						padding: 18,
						marginBottom: 18,
						color: themeStatus === 'dark' ? '#fff' : '#23263a',
						width: '100%',
					}}>
					<div style={{ fontWeight: 600, marginBottom: 8 }}>DNS Setup Help</div>
					<div style={{ fontSize: 15, marginBottom: 6 }}>
						Add the following CNAME records in your DNS provider:
					</div>
					<table style={{ width: '100%', fontSize: 15, marginBottom: 8 }}>
						<thead>
							<tr
								style={{
									background: themeStatus === 'dark' ? '#181a25' : '#eaf0ff',
								}}>
								<th
									style={{
										padding: 4,
										textAlign: 'left',
										color: themeStatus === 'dark' ? '#fff' : '#23263a',
									}}>
									Subdomain
								</th>
								<th
									style={{
										padding: 4,
										textAlign: 'left',
										color: themeStatus === 'dark' ? '#fff' : '#23263a',
									}}>
									Type
								</th>
								<th
									style={{
										padding: 4,
										textAlign: 'left',
										color: themeStatus === 'dark' ? '#fff' : '#23263a',
									}}>
									Value
								</th>
							</tr>
						</thead>
						<tbody>
							{Object.keys(customDomains).map((key) => (
								<tr key={key}>
									<td style={{ padding: 4 }}>{key}.YOURDOMAIN.com</td>
									<td style={{ padding: 4 }}>CNAME</td>
									<td style={{ padding: 4 }}>{key}.schedulerdesk.com</td>
								</tr>
							))}
						</tbody>
					</table>
					<div style={{ fontSize: 14, color: themeStatus === 'dark' ? '#aaa' : '#888' }}>
						<b>Note:</b> DNS changes may take up to 24 hours to propagate.
					</div>
				</div>
			)}
			<div style={{ textAlign: 'right', marginTop: 24, width: '100%' }}>
				<button
					type='button'
					className='onboarding-btn'
					style={{ background: '#aaa', marginRight: 12 }}
					onClick={() => {
						setCustomStatus({});
						setDomainStep('intro');
					}}>
					Back
				</button>
				<button
					type='button'
					className='onboarding-btn'
					disabled={!allCustomValid}
					onClick={() => setStep(4)}>
					Proceed to Next Step
				</button>
			</div>
		</div>
	);

	const renderDomainSetup = () => {
		if (domainStep === 'intro') return renderDomainIntro();
		if (domainStep === 'temp') return renderTempDomainFlow();
		if (domainStep === 'custom') return renderCustomDomainFlow();
		return null;
	};

	const renderReview = () => {
		const summaryBg = themeStatus === 'dark' ? '#23263a' : '#f8f9fa';
		const summaryBorder = themeStatus === 'dark' ? '1px solid #35395a' : '2px solid #eee';
		const logoBoxStyle = {
			display: 'inline-block',
			border: summaryBorder,
			borderRadius: 8,
			padding: 6,
			marginRight: 12,
			verticalAlign: 'middle',
			maxWidth: 120,
			maxHeight: 60,
		};
		const plan = selectedPlan !== null ? plans[selectedPlan] : null;
		return (
			<div style={{ maxWidth: 800, margin: '0 auto' }}>
				<h2 style={{ textAlign: 'center', marginBottom: 24 }}>Review & Confirm</h2>
				<div
					className='onboarding-summary'
					style={{ background: summaryBg, border: summaryBorder }}>
					<div
						style={{
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
						}}>
						<h4 style={{ margin: 0 }}>Business Information</h4>
						<button
							className='onboarding-btn'
							style={{ padding: '4px 18px', fontSize: 14, margin: 0 }}
							onClick={() => setStep(1)}>
							Edit
						</button>
					</div>
					<div>
						<b>Salon Name:</b> {businessInfo.salonName}
					</div>
					<div>
						<b>Owner/Manager:</b> {businessInfo.ownerName}
					</div>
					<div>
						<b>Address:</b> {businessInfo.address}
					</div>
					<div>
						<b>Country:</b> {businessInfo.country}
					</div>
					<div>
						<b>Contact Number:</b> {businessInfo.contactNumber}
					</div>
					<div>
						<b>Email:</b> {businessInfo.email}
					</div>
					<div>
						<b>Business Type:</b>{' '}
						{businessInfo.businessType === 'Other'
							? businessInfo.businessTypeOther
							: businessInfo.businessType}
					</div>
					<div>
						<b>Currency:</b> {businessInfo.currency}
					</div>
					<div>
						<b>Language:</b> {businessInfo.language}
					</div>
					<div style={{ marginTop: 8 }}>
						<b>Opening & Closing Hours:</b>
					</div>
					<ul style={{ margin: 0, paddingLeft: 18 }}>
						{businessInfo.hours.map((h, i) => (
							<li key={h.day}>
								{h.day}: {h.open || '--'} - {h.close || '--'}
							</li>
						))}
					</ul>
					<div style={{ marginTop: 8, marginBottom: 4 }}>
						<b>Site Logo-Light:</b>
					</div>
					{businessInfo.logoWhite && businessInfo.logoWhite.length > 0 ? (
						businessInfo.logoWhite.map((img) => (
							<span key={img.id} style={logoBoxStyle}>
								<img
									src={img.file}
									alt='Logo-Light'
									style={{ maxWidth: 100, maxHeight: 40 }}
								/>
							</span>
						))
					) : (
						<span style={{ color: '#888' }}>Not uploaded</span>
					)}
					<div style={{ marginTop: 8, marginBottom: 4 }}>
						<b>Site Logo-Dark:</b>
					</div>
					{businessInfo.logoDark && businessInfo.logoDark.length > 0 ? (
						businessInfo.logoDark.map((img) => (
							<span key={img.id} style={logoBoxStyle}>
								<img
									src={img.file}
									alt='Logo-Dark'
									style={{ maxWidth: 100, maxHeight: 40 }}
								/>
							</span>
						))
					) : (
						<span style={{ color: '#888' }}>Not uploaded</span>
					)}
					<div style={{ marginTop: 8, marginBottom: 4 }}>
						<b>Site Logo-Mobile:</b>
					</div>
					{businessInfo.logoMobile && businessInfo.logoMobile.length > 0 ? (
						businessInfo.logoMobile.map((img) => (
							<span key={img.id} style={logoBoxStyle}>
								<img
									src={img.file}
									alt='Logo-Mobile'
									style={{ maxWidth: 60, maxHeight: 60 }}
								/>
							</span>
						))
					) : (
						<span style={{ color: '#888' }}>Not uploaded</span>
					)}
				</div>
				<div
					className='onboarding-summary'
					style={{ background: summaryBg, border: summaryBorder }}>
					<div
						style={{
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
						}}>
						<h4 style={{ margin: 0 }}>Selected Plan</h4>
						<button
							className='onboarding-btn'
							style={{ padding: '4px 18px', fontSize: 14, margin: 0 }}
							onClick={() => setStep(2)}>
							Edit
						</button>
					</div>
					{plan ? (
						<>
							<div>
								<b>Plan:</b> {plan.name}
							</div>
							<div>
								<b>Price:</b> {plan.currency}
								{plan.price.toLocaleString()} / {plan.period}
							</div>
							<div>
								<b>Users:</b> {plan.users}
							</div>
							<div>
								<b>Features:</b>
								<ul style={{ margin: 0, paddingLeft: 18 }}>
									{plan.features.map((f, i) => (
										<li key={i}>{f}</li>
									))}
								</ul>
							</div>
							<div style={{ color: '#4d69fa', fontSize: 13 }}>{plan.note}</div>
						</>
					) : (
						<div>No plan selected.</div>
					)}
				</div>
				<div
					className='onboarding-summary'
					style={{ background: summaryBg, border: summaryBorder }}>
					<div
						style={{
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
						}}>
						<h4 style={{ margin: 0 }}>Domain Setup</h4>
						<button
							className='onboarding-btn'
							style={{ padding: '4px 18px', fontSize: 14, margin: 0 }}
							onClick={() => setStep(3)}>
							Edit
						</button>
					</div>
					<div>
						<b>Type:</b>{' '}
						{domainOption === 'custom' ? 'Custom Domain' : 'Temporary Domain'}
					</div>
					{domainOption === 'custom' ? (
						<>
							<div style={{ marginTop: 8 }}>
								<b>Custom Domains:</b>
							</div>
							<ul style={{ margin: 0, paddingLeft: 18 }}>
								{Object.keys(customDomains).map((key) => (
									<li key={key}>
										{key}: {customDomains[key] || '--'}
									</li>
								))}
							</ul>
						</>
					) : (
						<>
							<div style={{ marginTop: 8 }}>
								<b>Temporary Domains:</b>
							</div>
							<ul style={{ margin: 0, paddingLeft: 18 }}>
								{tempGenDomains ? (
									Object.keys(tempGenDomains).map((key) => (
										<li key={key}>
											{key}: {tempGenDomains[key]}
										</li>
									))
								) : (
									<li>Not generated</li>
								)}
							</ul>
						</>
					)}
				</div>
				<div className='onboarding-checkbox'>
					<input
						type='checkbox'
						id='review-accept'
						checked={reviewAccepted}
						onChange={(e) => setReviewAccepted(e.target.checked)}
						style={{ width: 18, height: 18 }}
					/>
					<label htmlFor='review-accept' style={{ fontWeight: 500, cursor: 'pointer' }}>
						I confirm that the above information is correct.
					</label>
				</div>
				<div className='onboarding-checkbox'>
					<input
						type='checkbox'
						id='terms-accept'
						checked={termsAccepted}
						onChange={(e) => setTermsAccepted(e.target.checked)}
						style={{ width: 18, height: 18 }}
					/>
					<label htmlFor='terms-accept' style={{ fontWeight: 500, cursor: 'pointer' }}>
						I have read and agree to the{' '}
						<a href='#' target='_blank' rel='noopener noreferrer'>
							Terms & Conditions
						</a>{' '}
						and{' '}
						<a href='#' target='_blank' rel='noopener noreferrer'>
							Privacy Policy
						</a>
						.
					</label>
				</div>
				<div style={{ textAlign: 'right', marginTop: 24 }}>
					<button
						type='button'
						className='onboarding-btn'
						style={{ background: '#aaa', marginRight: 12 }}
						onClick={() => setStep(3)}>
						Back
					</button>
					<button
						type='button'
						className='onboarding-btn'
						disabled={!(reviewAccepted && termsAccepted)}
						onClick={() => setStep(5)}>
						Proceed to Payment
					</button>
				</div>
			</div>
		);
	};

	const renderPayment = () => {
		const summaryBg = themeStatus === 'dark' ? '#23263a' : '#f8f9fa';
		const summaryBorder = themeStatus === 'dark' ? '1px solid #35395a' : '2px solid #eee';

		const plan = selectedPlan !== null ? plans[selectedPlan] : null;

		let price = plan ? plan.price : 0;
		let discount = 0;
		let periodLabel = 'per month';
		if (billingPeriod === '6months') {
			discount = 0.1; // 10% off
			price = plan ? plan.price * 6 * (1 - discount) : 0;
			periodLabel = 'for 6 months (10% off)';
		} else if (billingPeriod === 'yearly') {
			discount = 0.2; // 20% off
			price = plan ? plan.price * 12 * (1 - discount) : 0;
			periodLabel = 'per year (20% off)';
		} else {
			price = plan ? plan.price : 0;
			periodLabel = 'per month';
		}

		const MAX_FILES = 6;
		const MAX_SIZE_MB = 5;
		const ALLOWED_TYPES = [
			'image/jpeg',
			'image/png',
			'image/gif',
			'image/webp',
			'image/bmp',
			'image/svg+xml',
			'application/pdf',
		];

		const handleBankReceiptUpload = (e) => {
			setBankReceiptError('');
			const files = Array.from(e.target.files || []);
			if (!files.length) return;
			if (localBankReceipt.length + files.length > MAX_FILES) {
				setBankReceiptError(`Cannot upload more than ${MAX_FILES} files.`);
				return;
			}

			const existing = localBankReceipt.map((f) => f.name + '_' + f.size);
			const validFiles = files.filter((file) => {
				if (!ALLOWED_TYPES.includes(file.type)) {
					setBankReceiptError('Only images and PDF files are allowed.');
					return false;
				}
				if (file.size > MAX_SIZE_MB * 1024 * 1024) {
					setBankReceiptError(`File size must be under ${MAX_SIZE_MB}MB.`);
					return false;
				}
				if (existing.includes(file.name + '_' + file.size)) {
					setBankReceiptError('Duplicate file: ' + file.name);
					return false;
				}
				return true;
			});
			if (!validFiles.length) return;
			const fileReaders = validFiles.map((file) => {
				return new Promise((resolve) => {
					const reader = new FileReader();
					reader.onload = () => {
						const blob = new Blob([reader.result], { type: file.type });
						let url;
						if (file.type.startsWith('image/') || file.type === 'application/pdf') {
							url = URL.createObjectURL(file);
						} else {
							url = reader.result;
						}
						resolve({
							id: Date.now() + Math.random(),
							file: reader.result,
							name: file.name,
							type: file.type,
							size: file.size,
							url,
						});
					};
					reader.readAsArrayBuffer(file);
				});
			});
			Promise.all(fileReaders).then((images) => {
				setLocalBankReceipt((prev) => [...prev, ...images]);
			});
		};

		const handleDeleteReceipt = (id) => {
			setLocalBankReceipt((prev) => {
				const updated = prev.filter((img) => img.id !== id);
				if (updated.length === 0) setBankInputKey(Date.now());
				return updated;
			});
		};

		return (
			<div style={{ maxWidth: 700, margin: '0 auto' }}>
				<h2 style={{ textAlign: 'center', marginBottom: 24 }}>Complete Your Payment</h2>
				<div
					className='onboarding-summary'
					style={{ marginBottom: 24, background: summaryBg, border: summaryBorder }}>
					<h4 style={{ marginTop: 0 }}>Selected Plan</h4>
					{plan ? (
						<>
							<div>
								<b>Plan:</b> {plan.name}
							</div>
							<div>
								<b>Base Price:</b> {plan.currency}
								{plan.price.toLocaleString()} / month
							</div>
							<div>
								<b>Users:</b> {plan.users}
							</div>
							<div>
								<b>Features:</b>
								<ul style={{ margin: 0, paddingLeft: 18 }}>
									{plan.features.map((f, i) => (
										<li key={i}>{f}</li>
									))}
								</ul>
							</div>
							<div style={{ color: '#4d69fa', fontSize: 13 }}>{plan.note}</div>
						</>
					) : (
						<div>No plan selected.</div>
					)}
				</div>
				<div
					className='onboarding-summary'
					style={{ marginBottom: 24, background: summaryBg, border: summaryBorder }}>
					<h4 style={{ marginTop: 0 }}>Choose Billing Period</h4>
					<div style={{ display: 'flex', gap: 16, marginBottom: 12 }}>
						<button
							className={`onboarding-btn${
								billingPeriod === 'monthly' ? ' selected' : ''
							}`}
							style={{
								padding: '8px 24px',
								background: billingPeriod === 'monthly' ? '#4d69fa' : '#aaa',
							}}
							onClick={() => setBillingPeriod('monthly')}>
							Monthly
						</button>
						<button
							className={`onboarding-btn${
								billingPeriod === '6months' ? ' selected' : ''
							}`}
							style={{
								padding: '8px 24px',
								background: billingPeriod === '6months' ? '#4d69fa' : '#aaa',
							}}
							onClick={() => setBillingPeriod('6months')}>
							6 Months <span style={{ fontWeight: 500 }}>(10% off)</span>
						</button>
						<button
							className={`onboarding-btn${
								billingPeriod === 'yearly' ? ' selected' : ''
							}`}
							style={{
								padding: '8px 24px',
								background: billingPeriod === 'yearly' ? '#4d69fa' : '#aaa',
							}}
							onClick={() => setBillingPeriod('yearly')}>
							Yearly <span style={{ fontWeight: 500 }}>(20% off)</span>
						</button>
					</div>
					<div style={{ fontWeight: 600, fontSize: 18, marginTop: 8 }}>
						Total: {plan ? plan.currency : ''}
						{price.toLocaleString()}{' '}
						<span style={{ fontSize: 15, color: '#888', fontWeight: 400 }}>
							/{' '}
							{billingPeriod === 'monthly'
								? 'month'
								: billingPeriod === '6months'
								? '6 months'
								: 'year'}
						</span>
					</div>
				</div>
				<div
					className='onboarding-summary'
					style={{ marginBottom: 24, background: summaryBg, border: summaryBorder }}>
					<h4 style={{ marginTop: 0 }}>Choose Payment Method</h4>
					<div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
						<button
							className={`onboarding-btn${
								paymentMethod === 'trial' ? ' selected' : ''
							}`}
							style={{
								padding: '8px 24px',
								background: paymentMethod === 'trial' ? '#4d69fa' : '#aaa',
							}}
							onClick={() => setPaymentMethod('trial')}>
							Start your 7 days, free trial
						</button>
						<button
							className={`onboarding-btn${
								paymentMethod === 'bank' ? ' selected' : ''
							}`}
							style={{
								padding: '8px 24px',
								background: paymentMethod === 'bank' ? '#4d69fa' : '#aaa',
							}}
							onClick={() => setPaymentMethod('bank')}>
							Pay by Bank Transfer
						</button>
						<button
							className={`onboarding-btn${
								paymentMethod === 'paypal' ? ' selected' : ''
							}`}
							style={{
								padding: '8px 24px',
								background: paymentMethod === 'paypal' ? '#4d69fa' : '#aaa',
							}}
							onClick={() => setPaymentMethod('paypal')}>
							Pay by PayPal
						</button>
					</div>
					{paymentMethod === 'bank' && (
						<div style={{ marginTop: 12 }}>
							<div style={{ fontWeight: 500, marginBottom: 8 }}>
								Upload Bank Receipt(s):
							</div>
							<input
								key={bankInputKey}
								style={{
									marginBottom: bankReceiptError ? 4 : 12,
								}}
								type='file'
								accept='image/*,application/pdf'
								multiple
								onChange={handleBankReceiptUpload}
							/>
							{bankReceiptError && (
								<div style={{ color: 'red', fontSize: 13, marginBottom: 12 }}>
									{bankReceiptError}
								</div>
							)}
							<div
								style={{
									marginTop: 8,
									display: 'flex',
									flexWrap: 'wrap',
									gap: 12,
								}}>
								{localBankReceipt.map((img) => {
									const isPdf =
										img.type === 'application/pdf' ||
										(img.file &&
											img.file.startsWith &&
											img.file.startsWith('data:application/pdf'));
									const boxBg = themeStatus === 'dark' ? '#23263a' : '#f5f5f5';
									const boxBorder = themeStatus === 'dark' ? '#35395a' : '#eee';
									const fileNameColor = themeStatus === 'dark' ? '#fff' : '#333';
									return (
										<div
											key={img.id}
											style={{
												position: 'relative',
												display: 'inline-block',
											}}>
											<div
												style={{
													width: 120,
													height: 140,
													display: 'flex',
													flexDirection: 'column',
													alignItems: 'center',
													justifyContent: 'center',
													background: boxBg,
													border: `1px solid ${boxBorder}`,
													borderRadius: 8,
													cursor: 'pointer',
													overflow: 'hidden',
												}}
												title={
													isPdf
														? 'Click to preview PDF'
														: 'Click to preview image'
												}
												onClick={() =>
													window.open(
														img.url || img.file,
														'_blank',
														'noopener',
													)
												}>
												{isPdf ? (
													<span
														style={{
															fontSize: 40,
															color: '#d32f2f',
															margin: '16px 0 8px 0',
														}}>
														📄
													</span>
												) : (
													<div
														style={{
															width: 90,
															height: 70,
															margin: '12px 0 8px 0',
															borderRadius: 4,
															background: '#2222',
															overflow: 'hidden',
															display: 'flex',
															alignItems: 'center',
															justifyContent: 'center',
														}}>
														<img
															src={img.url || img.file}
															alt={img.name}
															style={{
																width: '100%',
																height: '100%',
																objectFit: 'cover',
																display: 'block',
															}}
														/>
													</div>
												)}
												<span
													style={{
														fontSize: 12,
														color: fileNameColor,
														wordBreak: 'break-all',
														textAlign: 'center',
														margin: '0 6px 0 6px',
														display: '-webkit-box',
														WebkitLineClamp: 2,
														WebkitBoxOrient: 'vertical',
														overflow: 'hidden',
														textOverflow: 'ellipsis',
														maxHeight: 32,
														lineHeight: '16px',
													}}>
													{img.name}
												</span>
											</div>
											<button
												type='button'
												style={{
													position: 'absolute',
													top: -8,
													right: -8,
													background: '#f44336',
													color: '#fff',
													border: 'none',
													borderRadius: '50%',
													width: 22,
													height: 22,
													cursor: 'pointer',
													fontWeight: 700,
												}}
												onClick={() => handleDeleteReceipt(img.id)}>
												×
											</button>
										</div>
									);
								})}
								{previewImg && (
									<div
										style={{
											position: 'fixed',
											top: 0,
											left: 0,
											width: '100vw',
											height: '100vh',
											background: 'rgba(0,0,0,0.7)',
											zIndex: 9999,
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
										}}
										onClick={() => setPreviewImg(null)}>
										<img
											src={previewImg}
											alt='Preview'
											style={{
												maxWidth: '90vw',
												maxHeight: '90vh',
												borderRadius: 8,
												boxShadow: '0 2px 16px #0008',
											}}
										/>
									</div>
								)}
							</div>
						</div>
					)}
					{paymentMethod === 'paypal' && (
						<div style={{ marginTop: 12 }}>
							<div style={{ fontWeight: 500, marginBottom: 8 }}>
								You will be redirected to PayPal to complete your payment.
							</div>
							<button
								className='onboarding-btn'
								style={{
									background: '#ffc439',
									color: '#111',
									marginTop: 8,
									fontWeight: 700,
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									fontSize: 18,
									border: 'none',
									boxShadow: '0 2px 8px #ffc43944',
									height: 48,
									width: 180,
									padding: 0,
								}}>
								<img src={Paypal} alt='PayPal Logo' style={{ height: '80%' }} />
							</button>
						</div>
					)}
					{paymentMethod === 'trial' && (
						<div style={{ marginTop: 12, color: '#4d69fa', fontWeight: 500 }}>
							You can start your free trial now. No payment required.
						</div>
					)}
				</div>
				<div style={{ textAlign: 'right', marginTop: 24 }}>
					<button
						type='button'
						className='onboarding-btn'
						style={{ background: '#aaa', marginRight: 12 }}
						onClick={() => setStep(4)}>
						Back
					</button>
					<button
						type='button'
						className='onboarding-btn'
						style={{ background: '#4d69fa' }}
						onClick={() => navigate('/')}>
						{paymentMethod === 'trial'
							? 'Start Free Trial'
							: paymentMethod === 'bank'
							? 'Submit Bank Transfer'
							: 'Proceed to PayPal'}
					</button>
				</div>
			</div>
		);
	};

	const renderStepContent = () => {
		if (step === 0) return renderWelcome();
		if (step === 1) return renderBusinessInfo();
		if (step === 2) return renderChoosePlan();
		if (step === 3) return renderDomainSetup();
		if (step === 4) return renderReview();
		if (step === 5) return renderPayment();
		return <div></div>;
	};

	const renderChoosePlan = () => (
		<div style={{ maxWidth: 1000, margin: '0 auto' }}>
			<h2 style={{ textAlign: 'center', marginBottom: 24 }}>Choose Your Plan</h2>
			<div className='onboarding-card-grid'>
				{plans.map((plan, idx) => {
					const cardBg =
						themeStatus === 'dark'
							? selectedPlan === idx
								? '#23263a'
								: '#181a25'
							: selectedPlan === idx
							? '#eaf0ff'
							: '#f8f9fa';
					const cardBorder = plan.highlight
						? '2px solid #4d69fa'
						: selectedPlan === idx
						? '2px solid #4d69fa'
						: '2px solid transparent';
					const cardShadow = plan.highlight ? '0 4px 24px #4d69fa22' : undefined;
					const titleColor = themeStatus === 'dark' ? '#fff' : '#23263a';

					return (
						<div
							key={plan.name}
							className={`onboarding-card${selectedPlan === idx ? ' selected' : ''}${
								plan.highlight ? ' popular' : ''
							}`}
							style={{
								minWidth: 240,
								maxWidth: 270,
								background: cardBg,
								border: cardBorder,
								boxShadow: cardShadow,
							}}>
							<div
								className='plan-title'
								style={{ fontWeight: 700, fontSize: 20, color: titleColor }}>
								{plan.name}{' '}
								{plan.highlight && (
									<span style={{ color: '#4d69fa', fontSize: 13, marginLeft: 4 }}>
										(Most Popular)
									</span>
								)}
							</div>
							<div style={{ color: '#888', fontSize: 13, marginBottom: 4 }}>
								{plan.users} Registered Users
							</div>
							<div className='plan-price' style={{ fontSize: 28, marginBottom: 0 }}>
								{plan.currency}
								{plan.price.toLocaleString()}{' '}
								<span style={{ fontSize: 14, color: '#888', fontWeight: 400 }}>
									/ {plan.period}
								</span>
							</div>
							<ul
								className='plan-features'
								style={{ margin: '16px 0 12px 0', padding: 0, listStyle: 'none' }}>
								{plan.features.map((f, i) => (
									<li
										key={i}
										style={{ marginBottom: 6, color: '#888', fontSize: 15 }}>
										{f}
									</li>
								))}
							</ul>
							<div style={{ fontSize: 13, color: '#4d69fa', marginBottom: 8 }}>
								{plan.note}
							</div>
							<button
								type='button'
								className='onboarding-btn'
								style={{
									width: '100%',
									background: selectedPlan === idx ? '#4d69fa' : '#6c757d',
									color: '#fff',
									margin: 0,
									fontWeight: 600,
								}}
								onClick={() => setSelectedPlan(idx)}>
								{plan.button}
							</button>
						</div>
					);
				})}
			</div>
			<div style={{ textAlign: 'right', marginTop: 32 }}>
				<button
					type='button'
					className='onboarding-btn'
					style={{ background: '#aaa', marginRight: 12 }}
					onClick={() => setStep(1)}>
					Back
				</button>
				<button
					type='button'
					className='onboarding-btn'
					disabled={selectedPlan === null}
					onClick={() => setStep(3)}>
					Next
				</button>
			</div>
		</div>
	);

	const validateBusinessInfo = () => {
		const newErrors = {};
		if (!businessInfo.salonName || businessInfo.salonName.trim().length < 2) {
			newErrors.salonName = '*Required';
		}
		if (!businessInfo.ownerName || businessInfo.ownerName.trim().length < 2) {
			newErrors.ownerName = '*Required';
		}
		if (!businessInfo.address || businessInfo.address.trim().length < 5) {
			newErrors.address = '*Required';
		}
		if (!businessInfo.country) {
			newErrors.country = '*Required';
		}
		if (!businessInfo.contactNumber) {
			newErrors.contactNumber = '*Required';
		} else if (!/^\+?\d{7,15}$/.test(businessInfo.contactNumber.replace(/\s/g, ''))) {
			newErrors.contactNumber = 'Enter a valid phone number';
		}
		if (!businessInfo.email) {
			newErrors.email = '*Required';
		} else if (!/^\S+@\S+\.\S+$/.test(businessInfo.email)) {
			newErrors.email = 'Enter a valid email address';
		}
		if (!businessInfo.businessType) {
			newErrors.businessType = '*Required';
		} else if (
			businessInfo.businessType === 'Other' &&
			!businessInfo.businessTypeOther.trim()
		) {
			newErrors.businessTypeOther = '*Required';
		}
		if (!businessInfo.currency) {
			newErrors.currency = '*Required';
		}
		if (!businessInfo.language) {
			newErrors.language = '*Required';
		}

		return newErrors;
	};

	const renderBusinessInfo = () => (
		<form
			style={{ maxWidth: '100%', margin: '0 auto' }}
			onSubmit={(e) => {
				e.preventDefault();
				const validation = validateBusinessInfo();
				if (Object.keys(validation).length > 0) {
					setErrors(validation);
					return;
				}
				setStep(2);
			}}>
			<h2 style={{ textAlign: 'center', marginBottom: 24 }}>Business Information</h2>
			<div className='onboarding-grid'>
				<div style={{ marginTop: 18 }}>
					<label>Salon Name*</label>
					<input
						className='form-control mb-2 mt-2'
						name='salonName'
						value={businessInfo.salonName}
						onChange={handleBusinessInfoChange}
						required
						style={errors.salonName ? { border: '2px solid red' } : {}}
					/>
					{errors.salonName && (
						<div style={{ color: 'red', fontSize: 12 }}>{errors.salonName}</div>
					)}
				</div>
				<div style={{ marginTop: 18 }}>
					<label>Owner/Manager Name*</label>
					<input
						className='form-control mb-2 mt-2'
						name='ownerName'
						value={businessInfo.ownerName}
						onChange={handleBusinessInfoChange}
						required
						style={errors.ownerName ? { border: '2px solid red' } : {}}
					/>
					{errors.ownerName && (
						<div style={{ color: 'red', fontSize: 12 }}>{errors.ownerName}</div>
					)}
				</div>
				<div style={{ gridColumn: 'span 2', marginTop: 18 }}>
					<label>Business Address*</label>
					<input
						className='form-control mb-2 mt-2'
						name='address'
						value={businessInfo.address}
						onChange={handleBusinessInfoChange}
						required
						style={errors.address ? { border: '2px solid red' } : {}}
					/>
					{errors.address && (
						<div style={{ color: 'red', fontSize: 12 }}>{errors.address}</div>
					)}
				</div>
				<div style={{ marginTop: 18 }}>
					<label>Country*</label>
					<input
						className='form-control mb-2 mt-2'
						name='country'
						value={businessInfo.country}
						onChange={handleBusinessInfoChange}
						required
						style={errors.country ? { border: '2px solid red' } : {}}
					/>
					{errors.country && (
						<div style={{ color: 'red', fontSize: 12 }}>{errors.country}</div>
					)}
				</div>
				<div style={{ marginTop: 18 }}>
					<label>Contact Number*</label>
					<input
						className='form-control mb-2 mt-2'
						name='contactNumber'
						value={businessInfo.contactNumber}
						onChange={handleBusinessInfoChange}
						required
						style={errors.contactNumber ? { border: '2px solid red' } : {}}
					/>
					{errors.contactNumber && (
						<div style={{ color: 'red', fontSize: 12 }}>{errors.contactNumber}</div>
					)}
				</div>
				<div style={{ marginTop: 18 }}>
					<label>WhatsApp Number</label>
					<input
						className='form-control mb-2 mt-2'
						name='whatsappNumber'
						value={businessInfo.whatsappNumber}
						onChange={handleBusinessInfoChange}
					/>
				</div>
				<div style={{ marginTop: 18 }}>
					<label>Email Address*</label>
					<input
						className='form-control mb-2 mt-2'
						name='email'
						value={businessInfo.email}
						onChange={handleBusinessInfoChange}
						required
						style={errors.email ? { border: '2px solid red' } : {}}
					/>
					{errors.email && (
						<div style={{ color: 'red', fontSize: 12 }}>{errors.email}</div>
					)}
				</div>
				<div style={{ marginTop: 18 }}>
					<label>Business Type*</label>
					<div>
						<Select
							className='form-control mb-2 mt-2'
							name='businessType'
							value={businessInfo.businessType}
							onChange={handleBusinessInfoChange}
							required
							placeholder='Select...'
							style={errors.businessType ? { border: '2px solid red' } : {}}>
							{businessTypes.map((t) => (
								<option key={t} value={t}>
									{t}
								</option>
							))}
						</Select>
					</div>
					{businessInfo.businessType === 'Other' && (
						<div style={{ marginTop: 8 }}>
							<input
								className='form-control mb-2 mt-2'
								name='businessTypeOther'
								value={businessInfo.businessTypeOther}
								onChange={handleBusinessInfoChange}
								placeholder='Please specify your business type'
								required
								style={errors.businessTypeOther ? { border: '2px solid red' } : {}}
							/>
							{errors.businessTypeOther && (
								<div style={{ color: 'red', fontSize: 12 }}>
									{errors.businessTypeOther}
								</div>
							)}
						</div>
					)}
					{errors.businessType && (
						<div style={{ color: 'red', fontSize: 12 }}>{errors.businessType}</div>
					)}
				</div>
				<div style={{ marginTop: 18 }}>
					<label>GST / VAT / Tax ID</label>
					<input
						className='form-control mb-2 mt-2'
						name='taxId'
						value={businessInfo.taxId}
						onChange={handleBusinessInfoChange}
					/>
				</div>
				<div style={{ marginTop: 18 }}>
					<label>Currency*</label>
					<div>
						<Select
							className='form-control mb-2 mt-2'
							name='currency'
							value={businessInfo.currency}
							onChange={handleBusinessInfoChange}
							required
							placeholder='Select...'
							style={errors.currency ? { border: '2px solid red' } : {}}>
							{currencies.map((c) => (
								<option key={c} value={c}>
									{c}
								</option>
							))}
						</Select>
					</div>
					{errors.currency && (
						<div style={{ color: 'red', fontSize: 12 }}>{errors.currency}</div>
					)}
				</div>
				<div style={{ marginTop: 18 }}>
					<label>Default Language*</label>
					<div>
						<Select
							className='form-control mb-2 mt-2'
							name='language'
							value={businessInfo.language}
							onChange={handleBusinessInfoChange}
							required
							placeholder='Select...'
							style={errors.language ? { border: '2px solid red' } : {}}>
							{languages.map((l) => (
								<option key={l} value={l}>
									{l}
								</option>
							))}
						</Select>
					</div>
					{errors.language && (
						<div style={{ color: 'red', fontSize: 12 }}>{errors.language}</div>
					)}
				</div>
				<div style={{ gridColumn: 'span 2', marginTop: 18 }}>
					<label
						style={{
							fontWeight: 600,
							fontSize: 16,
							marginBottom: 8,
							display: 'block',
						}}>
						Opening & Closing Hours (per day)
					</label>
					<div
						style={{
							marginBottom: 12,
							border: '1px solid #e0e0e0',
							borderRadius: 8,
							padding: 16,
						}}>
						<div
							style={{
								display: 'flex',
								fontWeight: 600,
								color: '#4d69fa',
								marginBottom: 8,
							}}>
							<div style={{ width: 120 }}>Day</div>
							<div style={{ width: 160 }}>Open</div>
							<div style={{ width: 160 }}>Close</div>
						</div>
						{businessInfo.hours.map((h, idx) => (
							<div
								key={h.day}
								style={{
									display: 'flex',
									alignItems: 'center',
									gap: 8,
									marginBottom: 8,
								}}>
								<div style={{ width: 120 }}>{h.day}</div>
								<TimePicker
									format='HH:mm'
									suffixIcon={
										<ClockCircleOutlined
											style={{
												color:
													themeStatus === 'dark' ? '#ffffff' : '#212529',
											}}
										/>
									}
									value={h.open ? dayjs(h.open, 'HH:mm') : null}
									onChange={(val) =>
										handleHoursChange(
											idx,
											'open',
											val ? val.format('HH:mm') : '',
										)
									}
									popupClassName={`custom-timepicker-dropdown`}
									className={`ant-picker-input ${
										themeStatus === 'dark' ? 'dark-theme' : 'light-theme'
									}`}
									style={{
										width: 125,
										backgroundColor:
											themeStatus === 'dark' ? '#212529' : '#f8f9fa',
										color: themeStatus === 'dark' ? '#ffffff' : '#212529',
									}}
									minuteStep={5}
									allowClear={false}
									placeholder='Open time'
								/>
								<span style={{ fontWeight: 600, color: '#888' }}>-</span>
								<TimePicker
									format='HH:mm'
									suffixIcon={
										<ClockCircleOutlined
											style={{
												color:
													themeStatus === 'dark' ? '#ffffff' : '#212529',
											}}
										/>
									}
									value={h.close ? dayjs(h.close, 'HH:mm') : null}
									onChange={(val) =>
										handleHoursChange(
											idx,
											'close',
											val ? val.format('HH:mm') : '',
										)
									}
									popupClassName={`custom-timepicker-dropdown`}
									className={`ant-picker-input ${
										themeStatus === 'dark' ? 'dark-theme' : 'light-theme'
									}`}
									style={{
										width: 125,
										backgroundColor:
											themeStatus === 'dark' ? '#212529' : '#f8f9fa',
										color: themeStatus === 'dark' ? '#ffffff' : '#212529',
									}}
									minuteStep={5}
									allowClear={false}
									placeholder='Close time'
								/>
								{idx > 0 && (
									<Button
										className='ms-2'
										color='danger'
										icon='trash'
										iconStyle={{ width: '14px', height: '14px' }}
										onClick={() => handleDeleteDay(idx)}
									/>
								)}
							</div>
						))}
						{businessInfo.hours.length < 7 && (
							<button
								type='button'
								className='onboarding-btn'
								style={{ padding: '4px 16px', fontSize: 14, marginTop: 4 }}
								onClick={handleAddDay}>
								+ Add Day
							</button>
						)}
					</div>
				</div>
				<div
					style={{
						gridColumn: 'span 2',
						display: 'flex',
						gap: 32,
						marginBottom: 8,
						marginTop: 18,
					}}>
					<ImageUploader
						label='Site Logo-Light'
						selectedImages={businessInfo.logoWhite}
						onImageSelect={handleLogoChange('logoWhite')}
						onImageDelete={() =>
							setBusinessInfo((prev) => ({ ...prev, logoWhite: [] }))
						}
						maxImages={1}
						minDimensions={{ width: 160, height: 40 }}
						showAddButton={businessInfo.logoWhite.length === 0}
						darkModeStatus={darkModeStatus}
						themeStatus={themeStatus}
					/>
					<ImageUploader
						label='Site Logo-Dark'
						selectedImages={businessInfo.logoDark}
						onImageSelect={handleLogoChange('logoDark')}
						onImageDelete={() => setBusinessInfo((prev) => ({ ...prev, logoDark: [] }))}
						maxImages={1}
						minDimensions={{ width: 160, height: 40 }}
						showAddButton={businessInfo.logoDark.length === 0}
						darkModeStatus={darkModeStatus}
						themeStatus={themeStatus}
					/>
					<ImageUploader
						label='Site Logo-Mobile'
						selectedImages={businessInfo.logoMobile}
						onImageSelect={handleLogoChange('logoMobile')}
						onImageDelete={() =>
							setBusinessInfo((prev) => ({ ...prev, logoMobile: [] }))
						}
						maxImages={1}
						minDimensions={{ width: 80, height: 80 }}
						showAddButton={businessInfo.logoMobile.length === 0}
						darkModeStatus={darkModeStatus}
						themeStatus={themeStatus}
					/>
				</div>
			</div>
			<div style={{ textAlign: 'right', marginTop: 24 }}>
				<button
					type='button'
					className='onboarding-btn'
					style={{ background: '#aaa', marginRight: 12 }}
					onClick={() => setStep(0)}>
					Back
				</button>
				{/* <button type='submit' className='onboarding-btn'>
					Next
				</button> */}
				<button type='button' className='onboarding-btn' onClick={() => setStep(2)}>
					Next
				</button>
			</div>
		</form>
	);
	return (
		<div className='bg-img'>
			<div
				className='g-3'
				style={{
					position: 'absolute',
					top: 8,
					right: 8,
					display: 'flex',
					justifyContent: 'flex-end',
				}}>
				<div className='col-md-auto'>
					<div className='row g-3'>
						<div className='col-auto'>
							<Popovers trigger='hover' desc='Dark / Light mode'>
								<Button
									{...styledBtn}
									icon={darkModeStatus ? 'DarkMode' : 'LightMode'}
									onClick={() => setDarkModeStatus((prev) => !prev)}
									aria-label='Toggle theme'
								/>
							</Popovers>
						</div>
						<div className='col-auto'>
							<Dropdown>
								<DropdownToggle hasIcon={false}>
									<Button
										{...styledBtn}
										icon={getLangWithKey(i18n.language)?.icon}
										aria-label='Change language'
										data-tour='lang-selector'
									/>
								</DropdownToggle>
								<DropdownMenu isAlignmentEnd data-tour='lang-selector-menu'>
									{Object.keys(LANG).map((i) => (
										<DropdownItem key={LANG[i].lng}>
											<Button
												icon={LANG[i].icon}
												onClick={() => changeLanguage(LANG[i].lng)}>
												{LANG[i].text}
											</Button>
										</DropdownItem>
									))}
								</DropdownMenu>
							</Dropdown>
						</div>
					</div>
				</div>
			</div>
			<Page
				className='p-0 mt-3'
				style={{
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					height: '100vh',
					width: '100vw',
					background: 'transparent',
				}}>
				<Card
					className='shadow-3d-dark'
					data-tour='login-page'
					style={{
						width: '80%',
						height: '90%',
						maxWidth: '98vw',
						maxHeight: '98vh',
						display: 'flex',
						flexDirection: 'column',
						justifyContent: 'flex-start',
						alignItems: 'stretch',
						padding: '0',
						overflow: 'hidden',
					}}>
					<div
						className='onboarding-stepper'
						style={{
							position: 'sticky',
							top: 0,
							zIndex: 10,
							background: darkModeStatus ? '#242731' : '#f9f9f9',
							padding: '32px 40px 0 40px',
							marginBottom: 0,
							borderTopLeftRadius: 16,
							borderTopRightRadius: 16,
						}}>
						{steps.map((s, i) => (
							<div
								key={s}
								className={`step ${i === step ? 'active' : ''} ${
									i < step ? 'done' : ''
								}`}>
								{s}
							</div>
						))}
					</div>
					<CardBody
						style={{
							flex: 1,
							overflow: 'auto',
							padding: '24px 40px 32px 40px',
							background: darkModeStatus ? '#05050c' : '#fff',
						}}>
						<div className='onboarding-step-content'>{renderStepContent()}</div>
					</CardBody>
				</Card>
			</Page>
		</div>
	);
};

export default OnboardingSetup;
